import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "lucide-react";
import DOMPurify from "dompurify";

interface RichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
}

export default function MeasureNotesEditor({ value, onChange, placeholder }: RichTextEditorProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (ref.current && ref.current.innerHTML !== value) {
            ref.current.innerHTML = DOMPurify.sanitize(value || "");
        }
    }, [value]);

    // Check active formatting at cursor position
    const updateActiveFormats = useCallback(() => {
        if (!ref.current) return;
        
        const formats = new Set<string>();
        
        try {
            if (document.queryCommandState('bold')) formats.add('bold');
            if (document.queryCommandState('italic')) formats.add('italic');
            if (document.queryCommandState('underline')) formats.add('underline');
        } catch (error) {
            // Ignore errors from queryCommandState
        }
        
        setActiveFormats(formats);
    }, []);

    const exec = (cmd: string) => {
        if (cmd === 'insertUnorderedList' || cmd === 'insertOrderedList') {
            insertList(cmd === 'insertUnorderedList' ? 'ul' : 'ol');
        } else if (cmd === 'createLink') {
            insertLink();
        } else {
            document.execCommand(cmd, false);
            ref.current?.focus();
            updateActiveFormats();
        }
    };

    // Handle clicks on links to open them
    const handleClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'A') {
            e.preventDefault();
            const href = target.getAttribute('href');
            if (href) {
                window.open(href, '_blank', 'noopener,noreferrer');
            }
        }
    };

    // Handle paste to strip images and clean content
    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        
        const clipboardData = e.clipboardData;
        const pastedText = clipboardData.getData('text/plain');
        
        if (pastedText) {
            // Insert plain text only
            document.execCommand('insertText', false, pastedText);
            
            // Trigger change event
            if (onChange && ref.current) {
                onChange(ref.current.innerHTML);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Keyboard shortcuts
        if (e.ctrlKey || e.metaKey) {
            switch(e.key.toLowerCase()) {
                case 'b':
                    e.preventDefault();
                    exec('bold');
                    break;
                case 'i':
                    e.preventDefault();
                    exec('italic');
                    break;
                case 'u':
                    e.preventDefault();
                    exec('underline');
                    break;
            }
        }

        // Function to exit list - used by both Enter and Backspace
        const exitList = (listItem: HTMLElement, selection: Selection) => {
            const listNode = listItem.closest('ul, ol');
            if (!listNode) return;

            // 1. Create paragraph *after* the list
            const p = document.createElement('div');
            p.innerHTML = '<br>';
            listNode.parentNode?.insertBefore(p, listNode.nextSibling);

            // 2. Remove the empty li (and clean up list if necessary)
            listItem.remove();
            if (listNode.children.length === 0) {
                listNode.remove();  // list is now empty
            }

            // 3. Move caret to the new paragraph
            const newRange = document.createRange();
            newRange.setStart(p, 0);
            newRange.collapse(true);

            selection.removeAllRanges();
            selection.addRange(newRange);

            // 4. Notify React
            if (onChange && ref.current) {
                onChange(ref.current.innerHTML);
            }
        };

        // Handle Enter key in lists - ROBUST DOM manipulation approach
        if (e.key === 'Enter') {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const listItem = range.startContainer.parentElement?.closest('li');
                
                if (listItem && listItem.textContent?.trim() === '') {
                    // If we're in an empty list item, exit the list
                    e.preventDefault();
                    exitList(listItem, selection);
                }
            }
        }

        // Handle Backspace key in lists - same exit behavior as Enter
        if (e.key === 'Backspace') {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const listItem = range.startContainer.parentElement?.closest('li');
                
                // Check if cursor is at the start of an empty list item
                // Account for <br> tags that browsers insert (offset can be 0 or 1)
                if (listItem && listItem.textContent?.trim() === '' && (range.startOffset === 0 || range.startOffset === 1)) {
                    // If we're at the start of an empty list item, exit the list
                    e.preventDefault();
                    exitList(listItem, selection);
                }
            }
        }
    };

    const insertLink = () => {
        if (!ref.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            ref.current.focus();
            return;
        }

        const range = selection.getRangeAt(0);
        const selectedText = range.toString().trim();
        
        let url = prompt('Enter URL:', 'https://');
        if (!url) return;

        // Fix URL protocol issue - auto-prepend https:// if missing
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url;
        }

        let linkText = selectedText;
        if (!linkText) {
            linkText = prompt('Enter link text:', url) || url;
        }

        try {
            const link = document.createElement('a');
            link.href = url;
            link.textContent = linkText;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            
            range.deleteContents();
            range.insertNode(link);
            
            // Position cursor after the link
            const newRange = document.createRange();
            newRange.setStartAfter(link);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
            
            // Trigger change event
            if (onChange) {
                onChange(ref.current.innerHTML);
            }
        } catch (error) {
            console.error('Error inserting link:', error);
        }
        
        ref.current.focus();
    };

    const insertList = (listType: 'ul' | 'ol') => {
        if (!ref.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            ref.current.focus();
            return;
        }

        const range = selection.getRangeAt(0);
        const selectedText = range.toString().trim();
        
        // Create the list element
        const listElement = document.createElement(listType);
        
        if (selectedText) {
            // If text is selected, convert it to list items
            const lines = selectedText.split('\n').filter(line => line.trim());
            lines.forEach(line => {
                const li = document.createElement('li');
                li.textContent = line.trim();
                listElement.appendChild(li);
            });
        } else {
            // If no text selected, create a single empty list item
            const li = document.createElement('li');
            li.innerHTML = '&nbsp;'; // Non-breaking space to maintain cursor position
            listElement.appendChild(li);
        }

        try {
            // Delete the selected content and insert the list
            range.deleteContents();
            range.insertNode(listElement);
            
            // Position cursor at the end of the first list item
            const firstLi = listElement.querySelector('li');
            if (firstLi) {
                const newRange = document.createRange();
                newRange.setStart(firstLi, firstLi.childNodes.length);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
            }
            
            // Trigger change event
            if (onChange) {
                onChange(ref.current.innerHTML);
            }
        } catch (error) {
            console.error('Error inserting list:', error);
        }
        
        ref.current.focus();
    };

    return (
        <div className="rounded-md border bg-card">
            <div className="flex items-center gap-1 border-b p-2">
                <button 
                    type="button" 
                    className={`px-2 py-1 text-sm rounded transition-colors ${
                        activeFormats.has('bold') 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-muted'
                    }`}
                    onClick={() => exec('bold')} 
                    aria-label="Bold"
                >
                    <strong>B</strong>
                </button>
                <button 
                    type="button" 
                    className={`px-2 py-1 text-sm rounded italic transition-colors ${
                        activeFormats.has('italic') 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-muted'
                    }`}
                    onClick={() => exec('italic')} 
                    aria-label="Italic"
                >
                    I
                </button>
                <button 
                    type="button" 
                    className={`px-2 py-1 text-sm rounded underline transition-colors ${
                        activeFormats.has('underline') 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-muted'
                    }`}
                    onClick={() => exec('underline')} 
                    aria-label="Underline"
                >
                    U
                </button>
                <div className="w-px h-4 bg-border mx-1" />
                <button type="button" className="px-2 py-1 text-sm hover:bg-muted rounded transition-colors" onClick={() => exec('insertUnorderedList')} aria-label="Bulleted list">• Bullets</button>
                <button type="button" className="px-2 py-1 text-sm hover:bg-muted rounded transition-colors" onClick={() => exec('insertOrderedList')} aria-label="Numbered list"># Numbers</button>
                <div className="w-px h-4 bg-border mx-1" />
                <button type="button" className="px-2 py-1 text-sm hover:bg-muted rounded transition-colors flex items-center gap-1" onClick={() => exec('createLink')} aria-label="Insert link">
                    <Link className="h-3 w-3" />
                    Link
                </button>
            </div>
            <div
                ref={ref}
                className="min-h-28 max-h-60 overflow-auto p-3 text-sm outline-none [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-1 [&_a]:text-blue-600 [&_a]:underline hover:[&_a]:text-blue-800 [&_a]:cursor-pointer empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:italic"
                contentEditable
                data-placeholder={placeholder}
                onInput={(e) => {
                    onChange((e.target as HTMLDivElement).innerHTML);
                    updateActiveFormats();
                }}
                onKeyUp={updateActiveFormats}
                onMouseUp={updateActiveFormats}
                onKeyDown={handleKeyDown}
                onClick={handleClick}
                onPaste={handlePaste}
                suppressContentEditableWarning
                style={{ whiteSpace: 'pre-wrap' }}
            />
        </div>
    );
}